import { useNavigate } from "@tanstack/react-router";
import { CheckCircle2, FileText, Loader, MessageSquareText, ShieldCheck } from "lucide-react";
import { useState } from "react";

import { Alert, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { useSaveAgentFeedbackValidation } from "../../api/use-agent-feedback";
import type { ClaimWithRelations } from "../../api/use-claim";
import type { ClaimOverview } from "./agent-feedback-steps";
import { buildAdditionalLossPayload } from "./default-additional-loss-form";
import { SetBusinessTravelCompany } from "./set-business-travel-company";

interface Option {
  id: number;
  name: string;
  description: string;
  eligible: boolean | null;
}

/** Options of the confirmation radio — copied from FormEligibilityVerification.vue. */
const settings: Option[] = [
  {
    id: 1,
    name: "Confirmer le dossier",
    description: "Vous pourrez nous communiquer les préjudices sur le formulaire suivant",
    eligible: true,
  },
  {
    id: 5,
    name: "Je ferme le dossier",
    description: "Merci de nous indiquer la raison dans la note",
    eligible: false,
  },
];

const tabs = [
  { name: "Voyage d'Affaires", b2c: false },
  { name: "Voyage Loisir", b2c: true },
];

interface FormEligibilityVerificationProps {
  claim: ClaimWithRelations;
  claimOverview: ClaimOverview;
  onNextStep: (increment: number) => void;
}

/** Port of components/agent-feedback/FormEligibilityVerification.vue. */
export function FormEligibilityVerification({
  claim,
  claimOverview,
  onNextStep,
}: FormEligibilityVerificationProps) {
  const navigate = useNavigate();
  const saveValidation = useSaveAgentFeedbackValidation(claim.id);

  const [selected, setSelected] = useState<Option>(settings[0]);
  const [selectedTab, setSelectedTab] = useState(
    !claimOverview.isLeisureOffice && (claimOverview.isOfficeB2b || claimOverview.travelAgencyCompany)
      ? tabs[0]
      : tabs[1],
  );
  const [additionalLoss, setAdditionalLoss] = useState(false);
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const [openTerminationModal, setOpenTerminationModal] = useState(false);

  const customerNoteData = claim.customerNotes;

  function saveAgentFeedback(withDefaultAdditionalLoss: boolean) {
    return saveValidation.mutateAsync({
      payload: {
        optionId: selected.id,
        note: note || undefined,
        b2c: selectedTab.b2c,
      },
      defaultAdditionalLoss: withDefaultAdditionalLoss ? buildAdditionalLossPayload() : undefined,
    });
  }

  async function submitForm() {
    if (selected.eligible !== true) {
      setOpen(true); // Open modal to confirm non-eligibility
      return;
    }
    const skipAdditionalLossForm = !additionalLoss && selectedTab.b2c;
    try {
      await saveAgentFeedback(skipAdditionalLossForm);
      onNextStep(skipAdditionalLossForm ? 2 : 1);
    } catch {
      // Error toast handled by notify.promise.
    }
  }

  async function confirmAbandon() {
    setOpen(false);
    try {
      await saveAgentFeedback(false);
    } catch {
      // Error toast handled by notify.promise.
    }
    setOpenTerminationModal(true);
  }

  function closeTerminationModal() {
    setOpenTerminationModal(false);
    void navigate({ to: "/" });
  }

  return (
    <div>
      {/* Non-eligible confirmation — port of the "Abandonner le dossier" AlertModal */}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Abandonner le dossier</AlertDialogTitle>
            <AlertDialogDescription>
              Vous avez indiqué que ce dossier n'était pas éligible à indemnisation. Voulez-vous continuer ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button onClick={() => void confirmAbandon()}>Oui, je confirme</Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>

      {/* Port of the "Merci de votre retour" AlertModal */}
      <Dialog
        open={openTerminationModal}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            closeTerminationModal();
          }
        }}
      >
        <DialogPopup>
          <DialogHeader>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-6 w-6 text-green-600" aria-hidden="true" />
            </div>
            <DialogTitle className="text-center">Merci de votre retour</DialogTitle>
          </DialogHeader>
          <DialogPanel>
            <p className="text-sm text-gray-500">
              Aucune procédure d'indemnisation ne sera enclenchée auprès des passagers.
            </p>
            <p className="mt-4 text-sm text-gray-500 italic">
              Si vous pensez avoir fait une erreur, il est de renvoyer à nouveau le formulaire. Sinon, vous
              pouvez fermer cette page.
            </p>
          </DialogPanel>
          <DialogFooter>
            <Button className="w-full" onClick={closeTerminationModal}>
              Retourner au dashboard
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>

      <form onSubmit={(event) => event.preventDefault()}>
        <div className="space-y-12">
          {customerNoteData ? (
            <div className="border-b pt-2 pb-6">
              <h2 className="mb-5 inline-flex text-base leading-7 font-semibold text-gray-900">
                <MessageSquareText className="mt-1 mr-2 h-5 w-5 text-gray-700" />
                Notes Upclaim à destination de l'agence :
              </h2>
              <div className="mt-2">
                <blockquote className="prose mt-2 mb-3 px-6 text-base font-medium whitespace-pre-line">
                  « <span className="italic">{customerNoteData}</span> »
                </blockquote>
              </div>
            </div>
          ) : null}

          {/* Office ID loisir : ni type de prestation, ni choix d'entreprise (voyage loisir). */}
          {claimOverview.isLeisureOffice ? null : (
            <div className="border-b border-gray-900/10 pb-6">
              <div className="grid grid-cols-1">
                <div className="sm:block">
                  <h2 className="mb-5 inline-flex text-base leading-7 font-semibold text-gray-900">
                    <FileText className="mt-1 mr-2 h-5 w-5 text-gray-700" />
                    Type de prestation
                  </h2>
                  <nav className="isolate flex divide-x divide-gray-200 rounded-lg shadow" aria-label="Tabs">
                    {tabs.map((tab, tabIdx) => (
                      <button
                        key={tab.name}
                        type="button"
                        onClick={() => setSelectedTab(tab)}
                        aria-current={selectedTab.name === tab.name ? "page" : undefined}
                        className={cn(
                          selectedTab.name === tab.name
                            ? "bg-blue-50 text-gray-900"
                            : "text-gray-500 hover:text-gray-700",
                          tabIdx === 0 ? "rounded-l-lg" : "",
                          tabIdx === tabs.length - 1 ? "rounded-r-lg" : "",
                          "group relative min-w-0 flex-1 cursor-pointer overflow-hidden bg-white px-4 py-4 text-center text-sm font-medium hover:bg-gray-50 focus:z-10",
                        )}
                      >
                        <input
                          checked={selectedTab.name === tab.name}
                          readOnly
                          type="checkbox"
                          className="mr-3 -ml-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                        />
                        <span>{tab.name}</span>
                        <span
                          aria-hidden="true"
                          className={cn(
                            selectedTab.name === tab.name ? "bg-blue-600" : "bg-transparent",
                            "absolute inset-x-0 bottom-0 h-0.5",
                          )}
                        />
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
              {selectedTab.b2c ? (
                <div className="mt-10 px-6" />
              ) : (
                <div className="mt-4 p-6">
                  <p className="text-sm leading-7 font-medium text-gray-800">Entreprise :</p>
                  <SetBusinessTravelCompany
                    claimId={claim.id}
                    currentTravelAgencyCompany={
                      claim.legalSpecification.impactedSegment?.reservation.travelAgencyCompany ?? null
                    }
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-10">
          <div className="col-span-full gap-x-6 gap-y-8 sm:grid-cols-6">
            <h2 className="inline-flex text-base leading-7 font-semibold text-gray-900">
              <ShieldCheck className="mt-1 mr-2 h-5 w-5 text-gray-700" />
              Confirmer le dossier
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Afin d'écarter un éventuel dysfonctionnement technique.
            </p>
            <div
              className="mt-8 -space-y-px rounded-md bg-white"
              role="radiogroup"
              aria-label="Confirmer dossier"
            >
              {settings.map((setting, settingIdx) => {
                const checked = selected.id === setting.id;
                return (
                  <label
                    key={setting.name}
                    className={cn(
                      settingIdx === 0 ? "rounded-tl-md rounded-tr-md" : "",
                      settingIdx === settings.length - 1 ? "rounded-br-md rounded-bl-md" : "",
                      checked && settingIdx === 0 && "z-10 border-green-200 bg-green-50",
                      checked && settingIdx === 1 && "z-10 border-red-200 bg-red-50",
                      !checked && "border-gray-200",
                      "relative flex cursor-pointer border p-4 focus:outline-none",
                    )}
                  >
                    <input
                      type="radio"
                      name="agent-feedback-option"
                      className="sr-only"
                      checked={checked}
                      onChange={() => setSelected(setting)}
                    />
                    <span
                      className={cn(
                        checked
                          ? settingIdx === 0
                            ? "border-transparent bg-green-600"
                            : "border-transparent bg-red-600"
                          : "border-gray-300 bg-white",
                        "mt-0.5 flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full border",
                      )}
                      aria-hidden="true"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-white" />
                    </span>
                    <span className="ml-3 flex flex-col">
                      <span
                        className={cn(
                          checked ? (settingIdx === 0 ? "text-green-900" : "text-red-900") : "text-gray-900",
                          "block text-sm font-medium",
                        )}
                      >
                        {setting.name}
                      </span>
                      <span
                        className={cn(
                          checked ? (settingIdx === 0 ? "text-green-700" : "text-red-700") : "text-gray-500",
                          "block text-sm",
                        )}
                      >
                        {setting.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {selectedTab.b2c && selected.eligible ? (
          <div className="mt-6 py-10">
            {/* Port of ToggleWithDescription */}
            <div className="flex items-center justify-between gap-4">
              <span className="flex flex-grow flex-col">
                <Label htmlFor="additional-loss-toggle" className="text-sm font-medium text-gray-900">
                  Y a-t-il eu des préjudices supplémentaires ? (Hotel, ...)
                </Label>
                <span className="text-sm text-gray-500">
                  Si sélectionné, vous serez emmenés sur un formulaire dédié pour nous les communiquer.
                </span>
              </span>
              <Switch
                id="additional-loss-toggle"
                checked={additionalLoss}
                onCheckedChange={setAdditionalLoss}
              />
            </div>
            {additionalLoss ? (
              <div className="pt-6">
                <Alert variant="info">
                  <AlertTitle>Le formulaire de l'étape suivante doit alors nous être envoyé</AlertTitle>
                </Alert>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="mt-10">
          <Label htmlFor="agent-feedback-note" className="block text-base font-medium text-gray-900">
            Ajouter une note
          </Label>
          <div className="mt-2">
            <Textarea
              id="agent-feedback-note"
              rows={4}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-x-6">
          <Button type="submit" onClick={() => void submitForm()} disabled={saveValidation.isPending}>
            {saveValidation.isPending ? <Loader className="mr-2 h-4 w-4 animate-spin" /> : null}
            Envoyer
          </Button>
        </div>
      </form>
    </div>
  );
}
